import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  ActionIcon,
  Button,
  Divider,
  Group,
  Popover,
  ScrollArea,
  Stack,
  Switch,
  Text,
  useMantineColorScheme,
} from "@mantine/core";
import {
  formatCompactNumber,
  formatEnUsValue,
  useCurrency,
} from "../../../utils/helpers";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import * as d3 from 'd3';
import { exportTableToCsv } from "../../../utils/csvExport";
import { IconFilter } from "@tabler/icons-react";

const COST_TYPES = ["replacement","capex","salvage","fuel","opex","incentive"];
const COLOR_PALETTE = ["#033b8e","#ff7043","#51c7fe","#ff6e76","#4992ff","#0031f6"]; // reuses cashFlowChart.js's per-cost-type palette, aligned by index with COST_TYPES
const IGNORED_ASSET_KEYS = new Set(["building", "heatsource"]);

const normalizeAssetKey = (assetName) => `${assetName ?? ''}`.toLowerCase().replace(/[\s_-]/g, '');

const isIgnoredAsset = (assetName) => IGNORED_ASSET_KEYS.has(normalizeAssetKey(assetName));

const StackedBarChart = ({ stackedData, isAssetChecked, isAssetTable, enabledAssets, onEnabledAssetsChange }) => {
  const { colorScheme } = useMantineColorScheme();
  const containerRef = useRef(null);
  const legendRef = useRef(null);
  const tooltipRef = useRef(null);
  const svgElRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [hiddenSeries, setHiddenSeries] = useState(new Set());
  const [zoomRange, setZoomRange] = useState([0, 1]); // normalized [start,end] fraction of visible assets
  const dataKey = isAssetChecked ? 'assetDiscounted' : 'assetNominal';
  const fontFamily = 'Nunito, sans-serif';
  const currency = useCurrency();
  const safeStackedData = Array.isArray(stackedData) ? stackedData : [];
  const selectedAssets = Array.isArray(enabledAssets) ? enabledAssets : [];
  const canManageAssetFilter = typeof onEnabledAssetsChange === "function";

  // Zoom helpers used by HTML controls
  const zoomIn = () => {
    const [s, e] = zoomRange;
    const totalAssets = filteredAssets.length || 1;
    const rangeSpan = e - s;
    if (rangeSpan <= 1 / totalAssets) return;
    const newSpan = Math.max(rangeSpan / 2, 1 / totalAssets);
    const center = s + rangeSpan / 2;
    let ns = center - newSpan / 2;
    let ne = center + newSpan / 2;
    if (ns < 0) { ne -= ns; ns = 0; }
    if (ne > 1) { const diff = ne - 1; ns -= diff; ne = 1; }
    setZoomRange([Math.max(0, ns), Math.min(1, ne)]);
  };

  const zoomOut = () => {
    const [s, e] = zoomRange;
    const totalAssets = filteredAssets.length || 1;
    const rangeSpan = e - s;
    const newSpan = Math.min(rangeSpan * 2, 1);
    const center = s + rangeSpan / 2;
    let ns = center - newSpan / 2;
    let ne = center + newSpan / 2;
    if (ns < 0) { ne -= ns; ns = 0; }
    if (ne > 1) { const diff = ne - 1; ns -= diff; ne = 1; }
    setZoomRange([Math.max(0, ns), Math.min(1, ne)]);
  };

  const allAssetNames = useMemo(
    () => Array.from(new Set(safeStackedData.map((a) => a?.assetName).filter(Boolean))),
    [safeStackedData]
  );
  const availableAssets = useMemo(
    () => allAssetNames.filter((name) => !isIgnoredAsset(name)),
    [allAssetNames]
  );
  const filteredAssets = useMemo(
    () => availableAssets.filter((name) => selectedAssets.includes(name)),
    [availableAssets, selectedAssets]
  );

  const handleToggleAsset = useCallback((assetName, checked) => {
    if (!canManageAssetFilter) return;
    const next = checked
      ? Array.from(new Set([...selectedAssets, assetName]))
      : selectedAssets.filter((name) => name !== assetName);
    onEnabledAssetsChange(next);
  }, [canManageAssetFilter, selectedAssets, onEnabledAssetsChange]);

  const handleToggleAllAssets = useCallback((checked) => {
    if (!canManageAssetFilter) return;
    onEnabledAssetsChange(checked ? availableAssets : []);
  }, [canManageAssetFilter, onEnabledAssetsChange, availableAssets]);

  // Map asset name for display on axes/tooltips. Replace any 'battery' mention with 'BESS'.
  const displayAssetName = (name) => {
    if (!name) return name;
    if (/battery/i.test(name)) return 'BESS';
    return name;
  };

  function computeStacks(activeTypes){
    // Build structure: for each asset, positive and negative cumulative
    const stacks = []; // per type: array of {asset, type, value, y0, y1}
    const perAssetTotals = {}; // for tooltip total
    activeTypes.forEach(()=> stacks.push([]));
    filteredAssets.forEach(assetName => {
      const row = stackedData.find(r=> r.assetName===assetName);
      let pos = 0; let neg = 0;
      activeTypes.forEach((type, i) => {
        const v = row?.[dataKey]?.[type] ?? 0;
        if (v >= 0){
          const y0 = pos; const y1 = pos + v; pos = y1; stacks[i].push({asset:assetName,type,value:v,y0,y1});
        } else {
          const y0 = neg; const y1 = neg + v; neg = y1; stacks[i].push({asset:assetName,type,value:v,y0,y1});
        }
        perAssetTotals[assetName] = (perAssetTotals[assetName]||0)+v;
      });
    });
    const maxPos = d3.max(stacks.flat().map(d=> d.y1)) ?? 0;
    const minNeg = d3.min(stacks.flat().map(d=> d.y1)) ?? 0;
    return { stacks, extent: [minNeg, maxPos], perAssetTotals };
  }

  // Keep a ref to the latest draw so ResizeObserver always calls the current version
  const drawRef = useRef(null);

  function draw(){
    if (isAssetTable) return; // table mode, no chart
    const root = containerRef.current; if(!root) return;
    
    // Clear previous svg
    d3.select(root).select('svg').remove();
    const legendHost = legendRef.current;

    const activeTypes = COST_TYPES.filter(t=> !hiddenSeries.has(t));
    const { stacks, extent, perAssetTotals } = computeStacks(activeTypes);

    // Determine zoom indices
    const totalAssets = filteredAssets.length;
    if (totalAssets === 0) return;
    let [zs, ze] = zoomRange; zs = Math.max(0, Math.min(1, zs)); ze = Math.max(zs, Math.min(1, ze));
    let startIdx = Math.floor(zs * totalAssets);
    let endIdx = Math.ceil(ze * totalAssets);
    if (endIdx <= startIdx) endIdx = Math.min(totalAssets, startIdx+1);
    const visibleAssets = filteredAssets.slice(startIdx, endIdx);

    // Filter stack segments to visible assets
    const visibleStacks = stacks.map(layer => layer.filter(d=> visibleAssets.includes(d.asset)));

    // Get actual width accounting for browser zoom using getBoundingClientRect
    const rect = root.getBoundingClientRect();
    const width = rect.width > 0 ? rect.width : (root.clientWidth || 800);
    const height = 500;
    const margin = { top: 78, right: 24, bottom: 48, left: 60 };
    const innerW = Math.max(1, width - margin.left - margin.right);
    const innerH = height - margin.top - margin.bottom;

    const svg = d3.select(root).append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('background', colorScheme==='dark'? '#111111':'transparent')
      .style('font-family', fontFamily);
    svgElRef.current = svg.node();

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Title


    // Legend build (fixed order)
    if (legendHost){
      legendHost.innerHTML='';
      COST_TYPES.forEach((type,i)=> {
        const isHidden = hiddenSeries.has(type);
        const btn = document.createElement('button');
        btn.type='button';
        btn.style.display='flex'; btn.style.alignItems='center'; btn.style.gap='6px';
        btn.style.margin='0 10px 6px 0'; btn.style.padding='4px 10px';
        btn.style.fontSize='12px'; btn.style.fontFamily=fontFamily; btn.style.borderRadius='4px';
        btn.style.border='1px solid '+(colorScheme==='dark'? '#444':'#ccc');
        btn.style.background = colorScheme==='dark'? (isHidden? '#1c1c1c':'#222832') : (isHidden? '#f5f5f5':'#ffffff');
        btn.style.opacity = isHidden? .55:1; btn.style.cursor='pointer';
        btn.onmouseenter=()=> { if(!isHidden) btn.style.background = colorScheme==='dark'? '#2a303b':'#f0f6fa'; };
        btn.onmouseleave=()=> { btn.style.background = colorScheme==='dark'? (isHidden? '#1c1c1c':'#222832') : (isHidden? '#f5f5f5':'#ffffff'); };
        const sw = document.createElement('span');
        sw.style.width='14px'; sw.style.height='14px'; sw.style.borderRadius='3px'; sw.style.display='inline-block';
        sw.style.background = COLOR_PALETTE[i];
        sw.style.boxShadow='0 0 0 1px '+(colorScheme==='dark'? '#000':'#fff')+' inset, 0 0 0 1px rgba(0,0,0,0.15)';
        const label = document.createElement('span'); label.textContent = type.toUpperCase(); if(isHidden) label.style.textDecoration='line-through';
        btn.appendChild(sw); btn.appendChild(label);
        btn.onclick=()=> {
          const next = new Set(hiddenSeries); if (next.has(type)) next.delete(type); else next.add(type); setHiddenSeries(next);
        };
        legendHost.appendChild(btn);
      });
    }

    const x = d3.scaleBand().domain(visibleAssets).range([0, innerW]).padding(0.25);
    const y = d3.scaleLinear().domain([extent[0], extent[1]]).nice().range([innerH,0]);

    // Format x-axis ticks to show display names (e.g., 'Battery' -> 'BESS')
    const xAxis = d3.axisBottom(x).tickFormat(d => displayAssetName(d));
    const yAxis = d3.axisLeft(y).ticks(5).tickFormat(d=> formatCompactNumber(d));
    g.append('g').attr('transform', `translate(0,${innerH})`).call(xAxis)
      .call(sel=> sel.selectAll('text').attr('font-size',11).attr('font-weight',500).attr('fill', colorScheme==='dark'? '#d5d7e0':'#495057'))
      .call(sel=> sel.selectAll('line,path').attr('stroke','none'));
    g.append('g').call(yAxis)
      .call(sel=> sel.selectAll('text').attr('font-size',11).attr('font-weight',500).attr('fill', colorScheme==='dark'? '#d5d7e0':'#495057'))
      .call(sel=> sel.selectAll('line,path').attr('stroke','none'));

    // Axis labels
    g.append('text').attr('x', innerW/2).attr('y', innerH+38).attr('text-anchor','middle')
      .attr('fill', colorScheme==='dark'? '#d5d7e0':'#495057').attr('font-size',12).text('Assets');
    g.append('text').attr('transform','rotate(-90)').attr('x', -innerH/2).attr('y', -48).attr('text-anchor','middle')
      .attr('fill', colorScheme==='dark'? '#d5d7e0':'#495057').attr('font-size',12).text(`Cost (${currency})`);

    // Zero line if needed
    if (extent[0] < 0 && extent[1] > 0){
      g.append('line').attr('x1',0).attr('x2',innerW).attr('y1',y(0)).attr('y2',y(0))
        .attr('stroke', colorScheme==='dark'? '#888':'#999').attr('stroke-width',1);
    }

    // Tooltip
    const shouldRecreateTooltip =
      !tooltipRef.current ||
      !tooltipRef.current.isConnected ||
      tooltipRef.current.parentElement !== root;

    if (shouldRecreateTooltip){
      const t = document.createElement('div');
      t.style.position='absolute'; t.style.pointerEvents='none'; t.style.background = colorScheme==='dark'? '#222':'#fff';
      t.style.border='1px solid '+(colorScheme==='dark'? '#555':'#666'); t.style.padding='6px 8px'; t.style.fontSize='12px'; t.style.fontFamily=fontFamily;
      t.style.borderRadius='4px'; t.style.boxShadow='0 2px 6px rgba(0,0,0,.25)'; t.style.display='none'; t.style.zIndex=20;
      tooltipRef.current = t; root.appendChild(t); root.style.position='relative';
    }
    const tooltip = tooltipRef.current; tooltip.innerHTML='';

    // Draw stacks: for each layer create group rects
    visibleStacks.forEach((layer,i) => {
      g.selectAll('.bar-layer-'+i).data(layer).enter().append('rect')
        .attr('class','bar-layer-'+i+' stack-seg')
        .attr('x', d=> x(d.asset))
        .attr('width', x.bandwidth())
        .attr('y', d=> y(Math.max(d.y0, d.y1)))
        .attr('height', d=> Math.abs(y(d.y0) - y(d.y1)))
        .attr('fill', d => COLOR_PALETTE[COST_TYPES.indexOf(d.type)])
        .on('mousemove', (evt,d)=> {
          // Highlight all segments of the same cost type across every asset
          g.selectAll('rect.stack-seg')
            .attr('opacity', seg => seg.type === d.type ? 1 : 0.22)
            .attr('stroke-width', seg => seg.type === d.type ? 1.5 : 0)
            .attr('stroke', seg => seg.type === d.type ? (colorScheme==='dark'? '#ffffffaa':'#00000055') : 'none');
          // Tooltip remains asset-focused; could be adapted to show aggregated type if desired
          const parts = visibleStacks.map(l=> l.find(xSeg=> xSeg.asset===d.asset)).filter(Boolean);
            const html = `<strong>${displayAssetName(d.asset)}</strong><br/>`+
            parts.map(seg=> `<span style=\"color:${COLOR_PALETTE[COST_TYPES.indexOf(seg.type)]}\">●</span> ${seg.type.toUpperCase()}: <b>${currency} ${formatEnUsValue(seg.value)}</b>`).join('<br/>')+
            `<br/><em>Total: ${currency} ${formatEnUsValue(perAssetTotals[d.asset])}</em>`;
          tooltip.innerHTML = html;
          const bounds = root.getBoundingClientRect();
          tooltip.style.left = (evt.clientX - bounds.left + 12)+'px';
          tooltip.style.top = (evt.clientY - bounds.top - 8)+'px';
          tooltip.style.display='block';
        })
        .on('mouseleave', ()=> {
          tooltip.style.display='none';
          g.selectAll('rect.stack-seg')
            .attr('opacity', 1)
            .attr('stroke','none')
            .attr('stroke-width',0);
        });
    });

    // Zoom handled via HTML controls (rendered outside SVG)
  }

  // Always keep drawRef pointing to the latest draw function
  drawRef.current = draw;

  // Draw on prop/theme changes
  useEffect(()=> { draw(); }, [stackedData, isAssetChecked, colorScheme, enabledAssets, hiddenSeries, zoomRange, isAssetTable]);

  // Resize observer + window resize/zoom — use drawRef so the callback is never stale
  useEffect(()=> {
    const onResize = () => { if (drawRef.current) drawRef.current(); };

    if (!resizeObserverRef.current){
      resizeObserverRef.current = new ResizeObserver(onResize);
      if (containerRef.current) resizeObserverRef.current.observe(containerRef.current);
    }

    window.addEventListener('resize', onResize);
    
    return () => {
      window.removeEventListener('resize', onResize);
      if (resizeObserverRef.current && containerRef.current){
        resizeObserverRef.current.unobserve(containerRef.current);
      }
    };
  }, []);

  // Reset zoom when asset set changes
  useEffect(()=> { setZoomRange([0,1]); }, [selectedAssets.length]);

  // Cleanup tooltip node when switching to table view.
  useEffect(() => {
    if (isAssetTable && tooltipRef.current) {
      tooltipRef.current.remove();
      tooltipRef.current = null;
    }
  }, [isAssetTable]);

  // Prepare table data and columns for MantineReactTable
  const tableData = useMemo(() => {
    return safeStackedData.filter(a => selectedAssets.includes(a.assetName) && !isIgnoredAsset(a.assetName)).map(row => ({
      assetName: row.assetName,
      ...row[dataKey],
    }));
  }, [safeStackedData, selectedAssets, dataKey]);

  const tableColumns = useMemo(() => {
    return [
      { accessorKey: "assetName", header: "Asset Name", size: 150 },
      ...COST_TYPES.map(type => ({
        accessorKey: type,
        header: type.charAt(0).toUpperCase() + type.slice(1),
        size: 120,
        Cell: ({ cell }) => {
          const rawValue = cell.getValue();
          const numericValue = Number(rawValue);
          if (!Number.isFinite(numericValue)) return rawValue ?? "-";

          const compactValue = `${currency} ${formatCompactNumber(numericValue)}`;
          const fullValue = `${currency} ${formatEnUsValue(numericValue)}`;

          return <span title={fullValue}>{compactValue}</span>;
        },
      })),
    ];
  }, []);

  const tableInstance = useMantineReactTable({
    data: tableData,
    columns: tableColumns,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    initialState: { density: "xs" },
    renderTopToolbarCustomActions: ({ table }) => (
      <Group gap="xs">
        <Button
          size="xs"
          variant="default"
          onClick={() => exportTableToCsv(table, "asset_costs.csv")}
        >
          Export CSV
        </Button>
      </Group>
    ),
    mantineTableContainerProps: {
      sx: { height: tableData?.length > 9 ? "auto" : "500px" },
    },
    mantineTopToolbarProps: {
      sx: {
        backgroundColor: colorScheme === "light" ? "#FFFFFF" : "var(--se-background-alternative)",
      },
    },
    mantineBottomToolbarProps: {
      sx: {
        padding: "10px",
        backgroundColor: colorScheme === "light" ? "#FFFFFF" : "var(--se-background-alternative)",
      },
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: false,
      sx: {
        backgroundColor: colorScheme === "light" ? "#FFFFFF" : "var(--se-background-alternative)",
      },
    },
    mantineTableBodyProps: {
      sx: {
        backgroundColor: colorScheme === "light" ? "#FFFFFF" : "var(--se-background-alternative)",
      },
    },
    mantineTableBodyCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: colorScheme === "light" ? "#FFFFFF" : "var(--se-background-alternative)",
      },
    },
    mantineTableHeadCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: colorScheme === "light" ? "#FFFFFF" : "var(--se-background-alternative)",
      },
    },
  });

  return (
    <div style={{ width:'100%' }}>
      {!isAssetTable && (
        <div style={{ width:'100%', position:'relative', fontFamily:fontFamily, maxWidth:'100%', zoom:'118%' }} className="indicator-downloadable">
          <div style={{ display:'flex', justifyContent:'flex-end', gap:8, padding:'4px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Popover width={320} position="bottom-end" withArrow shadow="md">
                <Popover.Target>
                  <ActionIcon
                    variant="outline"
                    color={colorScheme === "dark" ? "gray" : "dark"}
                    title="Filter assets"
                    aria-label="Filter assets"
                  >
                    <IconFilter size={16} />
                  </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <Text size="sm" fw={600}>Asset Filter</Text>
                    <Switch
                      size="xs"
                      checked={availableAssets.length > 0 && selectedAssets.length === availableAssets.length}
                      indeterminate={selectedAssets.length > 0 && selectedAssets.length < availableAssets.length}
                      onChange={(e) => handleToggleAllAssets(e.currentTarget.checked)}
                      disabled={!canManageAssetFilter}
                      label="All"
                    />
                  </div>
                  <Divider my="xs" />
                  <ScrollArea.Autosize mah={220}>
                    <Stack spacing={6}>
                      {availableAssets.map((assetName) => (
                        <Switch
                          key={assetName}
                          size="sm"
                          label={displayAssetName(assetName)}
                          checked={selectedAssets.includes(assetName)}
                          onChange={(e) => handleToggleAsset(assetName, e.currentTarget.checked)}
                          disabled={!canManageAssetFilter}
                        />
                      ))}
                    </Stack>
                  </ScrollArea.Autosize>
                </Popover.Dropdown>
              </Popover>
              <button
                type="button"
                title="Zoom In"
                onClick={zoomIn}
                style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', background: colorScheme==='dark'? '#222832':'#ffffff', color: colorScheme==='dark'? '#d5d7e0':'#495057' }}
              >
                +
              </button>
              <button
                type="button"
                title="Zoom Out"
                onClick={zoomOut}
                style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', background: colorScheme==='dark'? '#222832':'#ffffff', color: colorScheme==='dark'? '#d5d7e0':'#495057' }}
              >
                -
              </button>
              <select
                title="Download"
                style={{ padding:'6px 10px', fontSize:'12px', border:'1px solid #ccc', borderRadius:'4px', cursor:'pointer', background: colorScheme==='dark'? '#222832':'#ffffff', color: colorScheme==='dark'? '#d5d7e0':'#495057' }}
                value=""
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'png') {
                    // Download PNG
                    const svgEl = svgElRef.current;
                    if (!svgEl) return;
                    const serializer = new XMLSerializer();
                    const cloned = svgEl.cloneNode(true);
                    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                    bg.setAttribute('x','0');
                    bg.setAttribute('y','0');
                    bg.setAttribute('width', svgEl.clientWidth || svgEl.getAttribute('width') || 1200);
                    bg.setAttribute('height', svgEl.clientHeight || svgEl.getAttribute('height') || 500);
                    bg.setAttribute('fill', colorScheme==='dark'? '#111111':'#ffffff');
                    cloned.insertBefore(bg, cloned.firstChild);
                    const svgString = serializer.serializeToString(cloned);
                    const img = new Image();
                    const canvas = document.createElement('canvas');
                    const w = svgEl.clientWidth || parseInt(svgEl.getAttribute('width'), 10) || 1200;
                    const h = svgEl.clientHeight || parseInt(svgEl.getAttribute('height'), 10) || 500;
                    canvas.width = w;
                    canvas.height = h;
                    const ctx = canvas.getContext('2d');
                    img.onload = () => {
                      ctx.drawImage(img, 0, 0);
                      canvas.toBlob((blob) => {
                        if (!blob) return;
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        const ts = new Date().toISOString().replace(/[:.]/g, '-');
                        a.download = `stacked-assets-${ts}.png`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(url);
                      });
                    };
                    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
                  } else if (value === 'csv') {
                    // Download CSV with Asset Name and all cost type columns
                    try {
                      // CSV-escape: wrap in quotes if value contains comma, quote, or non-ASCII
                      const csvEscape = (val) => {
                        const s = String(val);
                        if (/[,"\n\r]/.test(s) || /[^\x20-\x7E]/.test(s)) {
                          return '"' + s.replace(/"/g, '""') + '"';
                        }
                        return s;
                      };
                      const rows = [];
                      // Build header: Asset Name + each cost type with currency in header
                      const headerCols = ['Asset Name', ...COST_TYPES.map(t => `${t.charAt(0).toUpperCase() + t.slice(1)} (${currency})`)];
                      rows.push(headerCols.map(csvEscape).join(','));
                      // Build data rows - each row is an asset (raw numbers, currency is in header)
                      filteredAssets.forEach(assetName => {
                        const row = stackedData.find(r => r.assetName === assetName);
                        const vals = COST_TYPES.map(type => {
                          const v = row?.[dataKey]?.[type];
                          return v !== undefined ? v : '';
                        });
                        rows.push([csvEscape(assetName), ...vals].join(','));
                      });
                      const csvContent = rows.join('\n');
                      // Prepend UTF-8 BOM so Excel correctly renders Unicode chars (€, £, ¥, etc.)
                      const BOM = '\uFEFF';
                      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      const ts = new Date().toISOString().replace(/[:.]/g, '-');
                      a.download = `stacked-assets-${ts}.csv`;
                      a.href = url;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                    } catch (err) {
                      // Silent fail
                    }
                  }
                  // Reset dropdown
                  e.target.selectedIndex = 0;
                }}
              >
                <option value="" disabled hidden>Download</option>
                <option value="png">PNG</option>
                <option value="csv">CSV</option>
              </select>
            </div>
          </div>
          <div ref={legendRef} style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', padding:'6px 0 0 0', margin:'0 0 -4px 0' }} />
          <div ref={containerRef} style={{ height:500, width:'100%' }}  />
        </div>
      )}
      {isAssetTable && (
        <MantineReactTable table={tableInstance} />
      )}
    </div>
  );
};

export default StackedBarChart;
